import { useCallback, useState, useMemo, useEffect, useRef, type Ref } from "react";
import ReactGridLayout, { useContainerWidth, verticalCompactor } from "react-grid-layout";
import type { Layout, LayoutItem, EventCallback } from "react-grid-layout";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  type DragStartEvent,
  type DragEndEvent,
} from "@dnd-kit/core";
import { snapCenterToCursor } from "@dnd-kit/modifiers";
import { X, Minus, Plus, LayoutGrid, Trash2, Save, Copy, Info } from "lucide-react";
import { useConfirm } from "@omit/react-confirm-dialog";
import { Button } from "@/modern/components/ui/button-shadcn";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/modern/components/ui/select";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/modern/components/ui/tooltip";
import { Input } from "@/modern/components/ui/input";
import { useConfiguratorContext } from "../context/DashboardConfiguratorContext";
import { ConfiguratorTile } from "./ConfiguratorTile";
import { FONT_PRESETS, TILE_COLORS } from "../constants";
import type { FontPresetId, TileConfig } from "../types";
import type { WidgetId } from "@/other/constants/EntityManagerConfig";
import { HiddenTilesSidebar } from "./HiddenTilesSidebar";
import { cn } from "@/modern/utils";

interface ConfiguratorGridProps {
  onClose: () => void;
  onRegisterCloseHandler?: (handler: () => void) => void;
}

const ROW_HEIGHT = 100;
const GRID_MARGIN: [number, number] = [8, 8];
const MAX_ROW_SPAN = 5;
/** Matches the `p-4` (16px) padding on the containerRef div. */
const GRID_PADDING = 16;

/**
 * Convert the configurator's tile model to react-grid-layout's `Layout`.
 *
 * `backfillPositions` in `useDashboardConfigurator` ensures every visible tile
 * has x/y before reaching this point, so this is a pure shape conversion.
 */
function tilesToLayout(tiles: TileConfig[], columns: number): Layout {
  return tiles.map<LayoutItem>((tile) => ({
    i: tile.id,
    x: Math.min(tile.x ?? 0, columns - Math.min(tile.colSpan, columns)),
    y: tile.y ?? 0,
    w: Math.min(tile.colSpan, columns),
    h: Math.max(1, tile.rowSpan),
    minW: 1,
    minH: 1,
    maxW: columns,
    maxH: MAX_ROW_SPAN,
  }));
}

/**
 * Calculate the RGL grid cell (x, y) from an absolute pointer position.
 * Returns null if the pointer is outside the grid container.
 */
function pointerToGridCell(
  pointerX: number,
  pointerY: number,
  containerRect: DOMRect,
  gridWidth: number,
  columns: number
): { x: number; y: number } | null {
  const relX = pointerX - containerRect.left - GRID_PADDING;
  const relY = pointerY - containerRect.top - GRID_PADDING;

  // Reject drops outside the grid area
  if (relX < 0 || relY < 0 || pointerX > containerRect.right || pointerY > containerRect.bottom) {
    return null;
  }

  const cellW = (gridWidth - GRID_MARGIN[0] * (columns + 1)) / columns;
  const x = Math.max(0, Math.min(columns - 1, Math.floor((relX - GRID_MARGIN[0]) / (cellW + GRID_MARGIN[0]))));
  const y = Math.max(0, Math.floor((relY - GRID_MARGIN[1]) / (ROW_HEIGHT + GRID_MARGIN[1])));
  return { x, y };
}

export function ConfiguratorGrid({ onClose, onRegisterCloseHandler }: ConfiguratorGridProps) {
  const {
    columns,
    visibleTiles,
    hiddenTiles,
    activePresetId,
    activeFontPreset,
    activePreset,
    presets,
    isActivePresetDefault,
    hasDraftChanges,
    handleLayoutChange,
    handleToggleVisibility,
    handleMoveBetweenContainers,
    handleColumnChange,
    handleFontPresetChange,
    handleUpdatePreset,
    handleSaveAsNewPreset,
    handleDeletePreset,
    handleDiscard,
  } = useConfiguratorContext();

  const confirm = useConfirm();

  const handleDeleteWithConfirm = useCallback(async () => {
    const confirmed = await confirm({
      title: "Delete preset?",
      description: "Are you sure you want to delete this preset? This action cannot be undone.",
      customActions: ({ confirm: ok, cancel }: { confirm: () => void; cancel: () => void }) => (
        <><Button variant="ghost" size="sm" onClick={cancel}>Cancel</Button><Button variant="destructive" size="sm" onClick={ok}>Delete</Button></>
      ),
    });
    if (confirmed) handleDeletePreset(activePresetId);
  }, [confirm, handleDeletePreset, activePresetId]);

  const activePresetName = activePreset?.name ?? "Dashboard";

  const [presetName, setPresetName] = useState(activePresetName);
  const [nameError, setNameError] = useState<string | null>(null);

  useEffect(() => {
    setPresetName(activePresetName);
    setNameError(null);
  }, [activePresetId, activePresetName]);

  const handleNameChange = useCallback((value: string) => {
    setPresetName(value);
    if (nameError) setNameError(null);
  }, [nameError]);

  const validatePresetName = useCallback((name: string, excludeCurrentPreset: boolean = false): string | null => {
    const trimmed = name.trim();
    if (trimmed.length === 0) {
      return "Preset name cannot be empty";
    }
    const isDuplicate = presets.some(p =>
      (excludeCurrentPreset ? p.id !== activePresetId : true) &&
      p.name.toLowerCase() === trimmed.toLowerCase()
    );
    if (isDuplicate) {
      return "A preset with this name already exists";
    }
    return null;
  }, [presets, activePresetId]);

  const trimmedName = presetName.trim();
  const nameHasChanged = trimmedName !== activePresetName;
  const hasAnyChanges = hasDraftChanges || nameHasChanged;
  const showSaveAsNew = nameHasChanged && trimmedName.length > 0;

  /** RGL layout derived from the current visible tiles. */
  const layout = useMemo(
    () => tilesToLayout(visibleTiles, columns),
    [visibleTiles, columns]
  );

  /**
   * Id of the tile currently being dragged from the hidden sidebar via @dnd-kit.
   * Kept in state so the grid re-renders to show the drop area when empty.
   */
  const [draggingFromHidden, setDraggingFromHidden] = useState<WidgetId | null>(null);

  /** useContainerWidth (v2 recommended) provides reactive width measurement. */
  const { width, containerRef } = useContainerWidth({ initialWidth: 1000 });

  /**
   * Tracks whether a drag or resize interaction is currently in progress.
   * Stored in a ref (not state) so it never triggers a re-render.
   *
   * Without this guard, v2 RGL creates a feedback loop during active
   * interactions: onLayoutChange → setTiles → new layout prop → RGL compacts
   * again → onLayoutChange → … This blows React's maximum update depth in
   * the app's legacy ReactDOM.render mode.
   */
  const isInteractingRef = useRef(false);

  /**
   * Ref attached to the hidden sidebar root element. Used by the grid-drag
   * stop handler to detect whether the tile was released over the sidebar.
   */
  const sidebarRef = useRef<HTMLDivElement>(null);

  /**
   * Id of the visible tile currently being dragged within RGL.
   * Stored in a ref (not state) so the drag-stop handler always reads the
   * latest value without going through a re-render cycle.
   */
  const draggingGridTileRef = useRef<WidgetId | null>(null);

  /**
   * Whether a grid tile is actively being dragged. Kept in state (not just a
   * ref) so the sidebar re-renders to show the drop-target highlight.
   */
  const [draggingFromGrid, setDraggingFromGrid] = useState(false);

  /**
   * Wrapper ref used to locate the Radix scroll viewport for auto-scroll.
   */
  const scrollAreaWrapperRef = useRef<HTMLDivElement>(null);

  /**
   * RAF handle for the auto-scroll loop. Stored in a ref so it survives
   * re-renders without cancelling itself.
   */
  const autoScrollRafRef = useRef<number | null>(null);

  /** Latest pointer Y (client space) updated on every pointermove during drag. */
  const dragPointerYRef = useRef<number>(0);

  /**
   * Auto-scroll the Radix ScrollArea viewport while a grid tile is being
   * dragged. When the pointer is within SCROLL_ZONE pixels of the top or
   * bottom edge of the viewport the content scrolls proportionally, letting
   * the user drag tiles to rows that are currently off-screen.
   */
  useEffect(() => {
    const stop = () => {
      if (autoScrollRafRef.current !== null) {
        cancelAnimationFrame(autoScrollRafRef.current);
        autoScrollRafRef.current = null;
      }
    };

    if (!draggingFromGrid) {
      stop();
      return;
    }

    const SCROLL_ZONE = 80;
    const MAX_SPEED = 12;

    const onPointerMove = (e: PointerEvent) => {
      dragPointerYRef.current = e.clientY;
    };
    window.addEventListener("pointermove", onPointerMove, { passive: true });

    const tick = () => {
      const wrapper = scrollAreaWrapperRef.current;
      if (wrapper) {
        const viewport = wrapper;
        if (viewport) {
          const rect = viewport.getBoundingClientRect();
          const y = dragPointerYRef.current;
          const distTop = y - rect.top;
          const distBottom = rect.bottom - y;
          if (distTop > 0 && distTop < SCROLL_ZONE) {
            viewport.scrollTop -= MAX_SPEED * (1 - distTop / SCROLL_ZONE);
          } else if (distBottom > 0 && distBottom < SCROLL_ZONE) {
            viewport.scrollTop += MAX_SPEED * (1 - distBottom / SCROLL_ZONE);
          }
        }
      }
      autoScrollRafRef.current = requestAnimationFrame(tick);
    };
    autoScrollRafRef.current = requestAnimationFrame(tick);

    return () => {
      window.removeEventListener("pointermove", onPointerMove);
      stop();
    };
  }, [draggingFromGrid]);

  // -------------------------------------------------------------------------
  // @dnd-kit sensors for hidden-tile → grid drag
  // -------------------------------------------------------------------------

  /**
   * PointerSensor with a small activation distance prevents accidental drags
   * when the user clicks a hidden tile (e.g. the eye-icon button).
   * The eye button also stops pointer propagation on pointerdown as a
   * secondary guard.
   */
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  /** @dnd-kit drag started — record which hidden tile is in flight. */
  const handleDndDragStart = useCallback((event: DragStartEvent) => {
    setDraggingFromHidden(event.active.id as WidgetId);
  }, []);

  /**
   * @dnd-kit drag ended.
   *
   * Uses the activator event's starting pointer position + the total delta
   * to reconstruct the final pointer position, then maps it to a grid cell.
   * If the pointer lands inside the grid container, the tile is made visible
   * at the computed cell. Otherwise the drag is simply cancelled.
   *
   * This replaces the HTML5 dragend/drop event pair, avoiding all browser
   * race conditions around "mouseup while still moving".
   */
  const handleDndDragEnd = useCallback((event: DragEndEvent) => {
    const id = event.active.id as WidgetId;
    setDraggingFromHidden(null);

    if (!containerRef.current) return;

    const pointerX = (event.activatorEvent as PointerEvent).clientX + event.delta.x;
    const pointerY = (event.activatorEvent as PointerEvent).clientY + event.delta.y;
    const rect = containerRef.current.getBoundingClientRect();

    const cell = pointerToGridCell(pointerX, pointerY, rect, width, columns);
    if (!cell) return;

    handleMoveBetweenContainers(id, true, cell);
  }, [containerRef, width, columns, handleMoveBetweenContainers]);

  /**
   * Called by RGL after every layout change.
   * Skipped while a drag or resize is in progress — the library fires this
   * continuously during mouse movement, and propagating each event back
   * through our state creates a render loop (layout prop changes → RGL
   * recompacts → onLayoutChange → layout prop changes → …).
   * The final committed layout is captured by onDragStop / onResizeStop.
   */
  const onRGLLayoutChange = useCallback(
    (next: Layout) => {
      if (!isInteractingRef.current) {
        handleLayoutChange(next);
      }
    },
    [handleLayoutChange]
  );

  /**
   * RGL drag started — suspend layout propagation and record the tile being
   * dragged so the stop handler can identify it.
   */
  const onRGLDragStart: EventCallback = useCallback(
    (_layout, _old, item) => {
      isInteractingRef.current = true;
      draggingGridTileRef.current = (item?.i ?? null) as WidgetId | null;
      setDraggingFromGrid(true);
    },
    []
  );

  /**
   * RGL drag ended — resume layout propagation.
   * If the pointer was released over the hidden sidebar, hide the tile
   * instead of committing its new grid position.
   */
  const onRGLDragStop: EventCallback = useCallback(
    (_layout, _old, _item, _placeholder, event) => {
      isInteractingRef.current = false;
      setDraggingFromGrid(false);
      const id = draggingGridTileRef.current;
      draggingGridTileRef.current = null;
      if (!id || !sidebarRef.current) return;
      const { clientX, clientY } = event as MouseEvent;
      const rect = sidebarRef.current.getBoundingClientRect();
      if (clientX >= rect.left && clientX <= rect.right && clientY >= rect.top && clientY <= rect.bottom) {
        handleMoveBetweenContainers(id, false);
      }
    },
    [handleMoveBetweenContainers]
  );

  /** Resize started — suspend layout propagation only. */
  const onRGLResizeStart = useCallback(() => {
    isInteractingRef.current = true;
  }, []);

  /**
   * Resize ended — resume layout propagation.
   * The subsequent onLayoutChange persists the final size.
   */
  const onRGLResizeStop = useCallback(() => {
    isInteractingRef.current = false;
  }, []);

  const handleClose = useCallback(async () => {
    if (!hasAnyChanges) {
      onClose();
      return;
    }

    const result = await confirm({
      title: "Unsaved Changes",
      description: "Your layout changes will be lost if you close now.",
      customActions: ({ confirm: confirmAction, cancel }: { confirm: () => void; cancel: () => void }) => (
        <div className="flex w-full justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={cancel}>
            Cancel
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              handleDiscard();
              confirmAction();
            }}
          >
            Don't Save
          </Button>
          <Button
            size="sm"
            onClick={() => {
              const error = validatePresetName(trimmedName, true);
              if (error) {
                setNameError(error);
                cancel();
                return;
              }
              handleUpdatePreset(trimmedName);
              confirmAction();
            }}
          >
            Save Layout
          </Button>
        </div>
      ),
    });
    if (result) onClose();
  }, [hasAnyChanges, trimmedName, confirm, handleDiscard, handleUpdatePreset, validatePresetName, onClose]);

  useEffect(() => {
    onRegisterCloseHandler?.(handleClose);
  }, [onRegisterCloseHandler, handleClose]);

  /** The tile currently being dragged from the sidebar (for the DragOverlay). */
  const draggingTile = draggingFromHidden
    ? hiddenTiles.find((t) => t.id === draggingFromHidden) ?? null
    : null;

  return (
    <DndContext sensors={sensors} onDragStart={handleDndDragStart} onDragEnd={handleDndDragEnd}>
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b flex-shrink-0 gap-4">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <div className="flex flex-col gap-1 min-w-0 flex-1 max-w-sm">
              <label
                htmlFor="preset-name"
                className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium"
              >
                Preset Name
              </label>
              <div className="flex items-center gap-1.5 min-w-0">
                <Input
                  id="preset-name"
                  value={presetName}
                  onChange={(e) => handleNameChange(e.target.value)}
                  placeholder="Enter preset name..."
                  aria-invalid={!!nameError}
                  aria-describedby={nameError ? "preset-name-error" : undefined}
                  className={cn(
                    "h-9 min-w-0 flex-1 text-base font-medium transition-all",
                    "bg-muted/50 border-muted-foreground/20",
                    "focus:bg-background focus:border-primary",
                    nameError && "border-destructive focus:border-destructive"
                  )}
                />
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-accent/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      aria-label="Edit the name to save a customized layout preset"
                    >
                      <Info className="h-3.5 w-3.5" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="max-w-[220px] text-xs">
                    Edit the name to save a customized layout preset.
                  </TooltipContent>
                </Tooltip>
              </div>
              {nameError && (
                <span id="preset-name-error" className="text-[11px] text-destructive">
                  {nameError}
                </span>
              )}
            </div>
          </div>

          <div className="flex items-center gap-4 shrink-0">
            <div className="flex items-center gap-1.5">
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Cols</span>
              <div className="flex items-center h-8 border border-input rounded-md overflow-hidden bg-muted/30">
                <button
                  className="px-2 h-full border-r border-input hover:bg-accent disabled:opacity-40 disabled:cursor-not-allowed transition-colors inline-flex items-center text-foreground"
                  onClick={() => handleColumnChange(Math.max(1, columns - 1))}
                  disabled={columns <= 1}
                >
                  <Minus className="h-3 w-3" />
                </button>
                <span className="w-7 text-center text-xs font-semibold tabular-nums select-none text-foreground">
                  {columns}
                </span>
                <button
                  className="px-2 h-full border-l border-input hover:bg-accent disabled:opacity-40 disabled:cursor-not-allowed transition-colors inline-flex items-center text-foreground"
                  onClick={() => handleColumnChange(Math.min(4, columns + 1))}
                  disabled={columns >= 4}
                >
                  <Plus className="h-3 w-3" />
                </button>
              </div>
            </div>

            <div className="flex items-center gap-1.5">
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Font</span>
              <Select
                value={activeFontPreset}
                onValueChange={(val) => handleFontPresetChange(val as FontPresetId)}
              >
                <SelectTrigger className="!h-8 w-[100px] text-xs bg-muted/30">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FONT_PRESETS.map((preset) => (
                    <SelectItem key={preset.id} value={preset.id}>
                      {preset.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            {!isActivePresetDefault && (
              <>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                      onClick={handleDeleteWithConfirm}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="text-xs">
                    Delete this preset
                  </TooltipContent>
                </Tooltip>

                <div className="w-px h-6 bg-border" />
              </>
            )}

            {showSaveAsNew && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 gap-1.5 transition-all border-primary/50 bg-primary/5 hover:bg-primary/10 text-primary"
                    onClick={() => {
                      const error = validatePresetName(trimmedName, false);
                      if (error) {
                        setNameError(error);
                        return;
                      }
                      handleSaveAsNewPreset(trimmedName);
                      onClose();
                    }}
                  >
                    <Copy className="w-3.5 h-3.5" />
                    <span>Save Layout As</span>
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="max-w-[200px] text-xs">
                  {`Create a new preset called "${trimmedName}"`}
                </TooltipContent>
              </Tooltip>
            )}

            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size="sm"
                  className="h-8 gap-1.5"
                  onClick={() => {
                    const error = validatePresetName(trimmedName, true);
                    if (error) {
                      setNameError(error);
                      return;
                    }
                    handleUpdatePreset(trimmedName);
                    onClose();
                  }}
                  disabled={!hasAnyChanges}
                >
                  <Save className="w-3.5 h-3.5" />
                  <span>Save Layout</span>
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="max-w-[200px] text-xs">
                {hasAnyChanges
                  ? `Save changes to "${activePresetName}"`
                  : "No changes to save"}
              </TooltipContent>
            </Tooltip>

            <div className="w-px h-6 bg-border" />

            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={handleClose}>
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>

        <div ref={scrollAreaWrapperRef} className="flex-1 flex min-w-0 overflow-y-auto overflow-x-hidden">
          <div className={`flex-1 relative transition-all ${
            draggingFromGrid
              ? "overflow-visible z-50" // Unclips the drag ghost so it can overlap the sidebar
              : "z-20"
          }`}>
              {/*
               * containerRef is always mounted so useContainerWidth maintains a
               * stable measurement regardless of the visible/empty branch below.
               */}
              <div ref={containerRef as Ref<HTMLDivElement>} className="p-4 min-h-full">
                {visibleTiles.length > 0 || draggingFromHidden !== null ? (
                  <ReactGridLayout
                    className="layout"
                    width={width}
                    layout={layout}
                    gridConfig={{
                      cols: columns,
                      rowHeight: ROW_HEIGHT,
                      margin: GRID_MARGIN,
                      containerPadding: [0, 0],
                    }}
                    dragConfig={{ cancel: "[data-tile-action]" }}
                    resizeConfig={{ handles: ["se", "sw", "ne", "nw", "e", "w", "s", "n"] }}
                    compactor={verticalCompactor}
                    onLayoutChange={onRGLLayoutChange}
                    onDragStart={onRGLDragStart}
                    onDragStop={onRGLDragStop}
                    onResizeStart={onRGLResizeStart}
                    onResizeStop={onRGLResizeStop}
                  >
                    {visibleTiles.map((tile) => (
                      <div key={tile.id} className="rgl-tile">
                        <ConfiguratorTile
                          tile={tile}
                          onToggleVisibility={handleToggleVisibility}
                        />
                      </div>
                    ))}
                  </ReactGridLayout>
                ) : (
                  <div className="flex items-center justify-center h-full min-h-[400px]">
                    <div className="flex flex-col items-center gap-3 text-center">
                      <LayoutGrid className="size-8 text-muted-foreground/40" />
                      <div className="space-y-1">
                        <h3 className="text-sm font-medium text-muted-foreground">
                          No widgets on the board
                        </h3>
                        <p className="text-xs text-muted-foreground/70 max-w-[18rem]">
                          Drag widgets from the hidden panel or click the eye icon
                          to make them appear here.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
          </div>

          <HiddenTilesSidebar
            sidebarRef={sidebarRef}
            isDropTarget={draggingFromGrid}
          />
        </div>
      </div>

      {/*
       * DragOverlay renders in a portal above everything, so it is never
       * clipped by overflow:hidden ancestors. It follows the pointer and
       * provides visual feedback while dragging a hidden tile.
       * dropAnimation={null} skips the snap-back animation on drop/cancel
       * since the tile either appears in the grid or stays in the sidebar.
       */}
      <DragOverlay dropAnimation={null} modifiers={[snapCenterToCursor]}>
        {draggingTile ? <TileDragPreview tile={draggingTile} /> : null}
      </DragOverlay>
    </DndContext>
  );
}

/** Visual-only preview rendered inside DragOverlay while dragging a hidden tile. */
function TileDragPreview({ tile }: { tile: TileConfig }) {
  const bgColor = TILE_COLORS[tile.color] ?? TILE_COLORS.blue;
  return (
    <div
      className="rounded-md overflow-hidden select-none shadow-lg opacity-80 cursor-grabbing"
      style={{ width: 160, minHeight: 60 }}
    >
      <div className="absolute inset-0 rounded-md" style={{ backgroundColor: bgColor, opacity: 0.72 }} />
      <div className="relative h-full min-h-[60px] flex items-center justify-center px-3">
        <span className="text-[11px] font-medium text-white/90 truncate">{tile.title}</span>
      </div>
    </div>
  );
}
